import { useCallback, useEffect, useState, useMemo } from 'react';

import { QuickActionLogicProps } from '@components/suggestions/quickActions/types';

import { EmailMessageDto, EmailRespondRequestDtoRespondType } from '@/api/generated';
import useEmail from '@/hooks/useEmail';
import { launchErrorToast } from '@/utils/toast';

import useSetAction from './useSetAction';
import useUsersEmails from './useUsersEmails';

export enum EmailTypes {
  Reply = 'Reply',
  Forward = 'Forward'
}

const useEmailQuickActionLogic = ({ quickAction, index }: QuickActionLogicProps) => {
  const {
    emailThread,
    emailThreadLoading,
    emailThreadError,
    emailThreadMessagesLoading,
    emailThreadMessagesError,
    generateEmailRespond,
    isGenerateEmailRespondPending,
    isGenerateEmailRespondError,
    sendEmailMessage,
    getLastEmailContent,
    getThreadContent,
    getLastEmailSender,
    getLastEmailBody
  } = useEmail(quickAction.sourceId || '');

  const [emailType, setEmailType] = useState<EmailTypes>(EmailTypes.Reply);
  const [emailMessage, setEmailMessage] = useState<string>('');
  const lastEmailSender = getLastEmailSender();
  const { users, selectedUsers, setSelectedUsers } = useUsersEmails({
    participants: emailThread?.participants,
    selectedEmail: lastEmailSender ? lastEmailSender.email : undefined
  });

  const emailBody = useMemo(() => getLastEmailBody(), [getLastEmailBody, emailThread]);

  const actionFunction = useCallback(
    async (): Promise<EmailMessageDto | undefined> =>
      sendEmailMessage({
        to: selectedUsers.map(user => user.email || ''),
        subject: emailThread?.subject || '',
        body: emailMessage,
        forward: emailType === EmailTypes.Forward
      }),
    [emailMessage, emailThread?.subject, selectedUsers, sendEmailMessage, emailType]
  );

  useSetAction(
    actionFunction,
    index,
    isGenerateEmailRespondPending ||
      isGenerateEmailRespondError ||
      emailThreadMessagesLoading ||
      !selectedUsers.length
  );

  const emailContent = useMemo(() => getLastEmailContent(), [getLastEmailContent]);
  const threadContent = useMemo(() => getThreadContent(), [getThreadContent]);

  const reGenerateMessage = useCallback(() => {
    generateEmailRespond({
      emailContent,
      threadContent,
      respondType:
        emailType === EmailTypes.Reply
          ? ('reply' as EmailRespondRequestDtoRespondType)
          : ('forward' as EmailRespondRequestDtoRespondType)
    }).then(response => {
      setEmailMessage(response?.message || '');
    });
  }, [emailType, setEmailMessage, emailContent, threadContent, generateEmailRespond]);

  useEffect(() => {
    if (!emailThreadLoading && emailContent && threadContent) {
      reGenerateMessage();
    }
  }, [emailType, reGenerateMessage, emailThreadLoading, emailContent, threadContent]);

  useEffect(() => {
    if (isGenerateEmailRespondError || emailThreadError || emailThreadMessagesError) {
      launchErrorToast();
    }
  }, [isGenerateEmailRespondError, emailThreadError, emailThreadMessagesError]);

  return {
    emailThread,
    emailThreadLoading,
    isGenerateEmailRespondPending,
    emailType,
    setEmailType,
    emailMessage,
    setEmailMessage,
    users,
    selectedUsers,
    setSelectedUsers,
    reGenerateMessage,
    getLastEmailSender,
    emailBody
  };
};

export default useEmailQuickActionLogic;
